import { ProductVariant } from 'types/Product';
import { FilteredSKUResult } from 'types/SelectedSkuTypes';
import { isNotInStock } from '../util';

type SelectedOptions = Record<string, string>;

/**
 * Filters the SKU options based on the selected options and variant availability.
 *
 * @param variants - An array of variant objects.
 * @param selectedOptions - An object representing the selected options.
 * @returns An object containing the filtered SKU options.
 */
function filterSKU(variants: ProductVariant[], selectedOptions: SelectedOptions = {}): FilteredSKUResult {
  const filteredOptions: FilteredSKUResult = {};

  /**
   * Calculates the unique options for each variant in the given array.
   *
   * @param variants - An array of variant objects.
   * @returns An object containing the unique options for each variant.
   */
  const uniqueOptions = variants.reduce(
    (acc, variant) => {
      Object.entries(variant.options).forEach(([key, value]) => {
        if (!acc[String(key)]) {
          acc[String(key)] = new Set();
        }
        acc[String(key)].add(value);
      });
      return acc;
    },
    {} as { [key: string]: Set<string> },
  );

  // Helper function to check if an option is in stock for a given set of selected options
  /**
   *  Checks if an option is in stock for a given set of selected options.
   * @param option - The option to check.
   * @param selected  - The selected options.
   * @returns - A boolean indicating if the option is in stock.
   */
  function isOptionInStock(option: string, selected: Record<string, string>): boolean {
    return variants.some((variant) => {
      if (
        !isNotInStock(variant.stock?.code) &&
        Object.entries(selected).every(([key, value]) => variant.options[key as keyof typeof variant.options] === value)
      ) {
        if (
          !variant.options[option as keyof typeof variant.options] ||
          variant.options[option as keyof typeof variant.options] === selected[String(option)]
        ) {
          return true;
        }
      }
      return false;
    });
  }

  // Step 1: Iterate through unique options and set stock status based on variant availability
  // Step 2: Populate the filtered options object with the unique options and stock status
  const keys = Object.keys(uniqueOptions);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[Number(i)];
    const values = Array.from(uniqueOptions[String(key)]);
    filteredOptions[String(key)] = [];

    for (let j = 0; j < values.length; j += 1) {
      const value = values[Number(j)];
      const disabled = !isOptionInStock(key, { ...selectedOptions, [key]: value });
      if (Array.isArray(filteredOptions[String(key)])) {
        (filteredOptions[String(key)] as Array<{ value: string; disabled: boolean }>).push({
          value,
          disabled,
        });
      }
    }
  }

  if (Array.isArray(filteredOptions.colour) && filteredOptions.colour.length < 2) {
    delete filteredOptions.colour;
  }

  return filteredOptions;
}

export default filterSKU;
